import type { KambiCouponValidateFailResponse } from '@mobi/api-types/kambi/player/coupon'
import type { KambiSportsSelection } from '@mobi/betslip/types'
import { isApiServiceError } from '@classic/Foundation/Services/ApiService'
import {
  postPlaceKambiSportsBet,
  postValidateKambiSportsBet,
} from '@core/Areas/KambiSports/helpers/api/betting'
import {
  ConfirmBet,
  ConfirmKambiBetFailed,
  ConfirmKambiBetSucceeded,
  ProposeBet,
  ProposeKambiBetFailed,
  ProposeKambiBetSucceeded,
} from '@core/Areas/Quickbet/signals'
import { createBetRequest } from './utils/createBetRequest'
import { handleResponseErrors } from './utils/handleResponseErrors'

/**
 * Handle kambi bet validation, checks for price change, betting closed etc
 * */
export const proposeKambiBetAsync = async ({ selection }: ProposeParams) => {
  ProposeBet()
  const request = createBetRequest({ selection, investmentWin: 1 })

  try {
    const response = await postValidateKambiSportsBet(request)

    if (response.status === 'SUCCESS') {
      ProposeKambiBetSucceeded()
      return
    }

    throw Error()
  } catch (error) {
    if (error instanceof Error && isApiServiceError(error)) {
      const response: KambiCouponValidateFailResponse = await error.response.json()
      handleResponseErrors({
        requestType: 'propose',
        request,
        response,
        responseHttpCode: error.response.status,
      })
    } else {
      ProposeKambiBetFailed()
    }
  }
}

/**
 * Handle kambi bet placement
 * */
export const confirmKambiBetAsync = async ({
  selection,
  investmentWin,
  copiedBetLoopPostId,
}: ConfirmParams) => {
  ConfirmBet()

  const baseRequest = createBetRequest({ selection, investmentWin })

  const request = copiedBetLoopPostId ? { ...baseRequest, copiedBetLoopPostId } : baseRequest

  try {
    const response = await postPlaceKambiSportsBet(request)

    if (response.status === 'SUCCESS') {
      ConfirmKambiBetSucceeded({ coupon: response.coupon })
      return
    }

    throw Error()
  } catch (error) {
    if (error instanceof Error && isApiServiceError(error)) {
      const response: KambiCouponValidateFailResponse = await error.response.json()
      handleResponseErrors({
        requestType: 'commit',
        request,
        response,
        responseHttpCode: error.response.status,
      })
    } else {
      ConfirmKambiBetFailed()
    }
  }
}

// =====
// Types
// =====

type ProposeParams = {
  selection: KambiSportsSelection
}

type ConfirmParams = {
  selection: KambiSportsSelection
  investmentWin: number
  copiedBetLoopPostId?: string
}
